import { useCallback, useMemo, useState } from "react"
import _t from "@core/i18n"
import type { ComparableProduct } from "~/fragments/comparableProduct"
import type { ComparableProductsPackage } from "~/types/squidex"
import type { PackageDropdown } from "../types"

/**
 * Tracks which product is selected in each table column and builds dropdown
 * configs for switching products within a package.
 *
 * When `packages` are provided each column shows one product from its package,
 * and the user can swap it via a dropdown. Already-selected products are hidden
 * from other columns' dropdowns to prevent duplicates.
 *
 * When `packages` are absent, returns `products` unchanged with empty dropdowns.
 */
export default function usePackageColumnsState(
  products: ComparableProduct[],
  packages?: ComparableProductsPackage[]
): [ComparableProduct[], PackageDropdown[]] {
  // All products grouped by package.
  const comparableProducts = useMemo<ComparableProduct[][]>(
    () =>
      packages?.map((packageItem) => packageItem.flatData.externalProducts) ??
      [],
    [packages]
  )

  // Currently selected product per column; initially the first product of each package.
  const [activePackagesProducts, setActivePackagesProducts] = useState<
    ComparableProduct[]
  >(
    () =>
      packages?.map(
        (packageItem) => packageItem.flatData.externalProducts[0]
      ) ?? []
  )

  const activeComparableProducts = packages ? activePackagesProducts : products

  // Replaces the product in the column identified by `productId`.
  const handleOnClick = useCallback(
    (newProduct: ComparableProduct, productId: string) => {
      setActivePackagesProducts((prev) =>
        prev.map((product) => (productId === product.id ? newProduct : product))
      )
    },
    []
  )

  const items = useMemo(
    () => (packages ? comparableProducts.flat() : []),
    [packages, comparableProducts]
  )

  const dropdowns: PackageDropdown[] = useMemo(
    () =>
      activePackagesProducts.map((product) => {
        const { id } = product

        // Keep the current column's product + any product not selected elsewhere.
        const filteredItems = items.filter(
          (item: ComparableProduct) =>
            item.id === id || !activePackagesProducts.includes(item)
        )

        return {
          label: _t("marketplace.changePackage"),
          items: filteredItems.map((item) => ({
            label: item.flatData.name,
            isActive: item.id === id,
            onClick: () => handleOnClick(item, id),
          })),
        }
      }),
    [activePackagesProducts, items, handleOnClick]
  )

  return [activeComparableProducts, dropdowns]
}
